import {
  ApartmentOutlined,
  AppstoreOutlined,
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
  FolderOpenOutlined,
  MoreOutlined,
  PlusOutlined,
  UserOutlined,
  RightOutlined,
} from '@ant-design/icons'
import {
  Button,
  DatePicker,
  Dropdown,
  Menu,
  Spin,
  Tooltip,
  Typography,
  message,
} from 'antd'
import { inject, observer } from 'mobx-react'
import queryString from 'query-string'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import AccessControlAction from '../../components/AccessControlAction'
import { AvatarHasName } from '../../components/Common/Avatar'
import ContentBlockWrapper from '../../components/ContentBlockWrapper'
import PageTitle from '../../components/PageTitle'
import { systemName } from '../../config'
import { ACL_ACTION_TYPE, ASSIGNEE_TYPE } from '../../constants'
import DashboardLayout from '../../layouts/DashboardLayout/DashboardLayout'
import { KpiV2Request } from '../../requests/KpiV2Request'
import KpiV2AddWeightModal from './KpiV2AddWeightModal'
import KpiV2DetailExpand from './KpiV2DetailExpand'
import KpiV2SubDetailExpand from './KpiV2SubDetailExpand'
import KpiV2SubUpsertModal from './KpiV2SubUpsertModal'
import KpiV2UpsertModal from './KpiV2UpsertModal'
import {
  TOKEN,
  TreeContainer,
  AspectCard,
  AspectHeaderRow,
  AspectHeaderLeft,
  AspectHeaderRight,
  AspectTitle,
  AspectWeightTag,
  AspectExpandedContent,
  AspectChildrenList,
  TreeNodeList,
  KpiNodeContainer,
  KpiRowCard,
  KpiRowContent,
  KpiRowTop,
  KpiChildrenList,
  ExpandIconWrapper,
  NodeTypeIcon,
  NodeTitle,
  NodeTitleText,
  NodeMeta,
  MetaItem,
  KgiBadge,
  HandlerWrap,
  ExpandedDetailContainer,
  MoreMenuTrigger,
  PageActions,
  KpiContentBlock,
  PageInner,
  TabBar,
  TabItem,
  TabLabel,
  FilterCard,
  FilterRow,
  FilterLabel,
  FilterDatePickerWrap,
  WeightBarText,
} from './KpiV2Styled'

const { Text } = Typography

// ── Tab config ─────────────────────────────────────────────────────────────────
const VIEW_TABS = [
  {
    label: 'KPI Công ty',
    value: 'COMPANY',
    // icon: <AppstoreOutlined />,
    aclType: ACL_ACTION_TYPE.tab_cong_ty__KPI,
    color: TOKEN.blue,
    // desc: 'Chỉ tiêu toàn công ty',
  },
  {
    label: 'KPI Phòng/Ban',
    value: 'DEPARTMENT',
    // icon: <ApartmentOutlined />,
    aclType: ACL_ACTION_TYPE.tab_phong_ban__KPI,
    color: TOKEN.purple,
    // desc: 'Chỉ tiêu theo phòng ban',
  },
  {
    label: 'KPI Cá nhân',
    value: 'PERSONAL',
    // icon: <UserOutlined />,
    aclType: ACL_ACTION_TYPE.tab_ca_nhan__KPI,
    color: TOKEN.teal,
    // desc: 'Chỉ tiêu cá nhân',
  },
]

const SCORE_COLOR = score => {
  if (score == null) return TOKEN.muted
  if (score >= 85) return TOKEN.green
  if (score >= 60) return TOKEN.amber
  return TOKEN.red
}

// ── WeightBar ──────────────────────────────────────────────────────────────────
const WeightBar = ({ name, value, color = TOKEN.blue }) => {
  if (value == null)
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        —
      </Text>
    )
  const displayValue = typeof value === 'number' ? value.toFixed(2) : value
  return (
    <WeightBarText $color={color}>
      {name}: {displayValue}%
    </WeightBarText>
  )
}

// ── Main Page ──────────────────────────────────────────────────────────────────
const KpiV2Page = props => {
  const { aclStore, authenticationStore, history, location } = props
  const queryStringParse = queryString.parse(location.search)
  const activeTab = queryStringParse.tab || null

  const isAdmin =
    authenticationStore.isAccountAdmin || authenticationStore.isSuperAdmin

  const visibleTabs = VIEW_TABS.filter(tab => {
    if (isAdmin) return true
    const entry = aclStore.aclActionsByUser.find(
      item => item.code === tab.aclType
    )
    return !!entry?.status
  })

  const effectiveTab = visibleTabs.length
    ? visibleTabs.find(t => t.value === activeTab)?.value ??
      visibleTabs[0]?.value
    : activeTab

  const handleTabChange = value => {
    setExpandedKpiId(null)
    setExpandedSubKpiId(null)
    history.replace(`${location.pathname}?tab=${value}`)
  }

  const [expandedKpiId, setExpandedKpiId] = useState(null)
  const [expandedSubKpiId, setExpandedSubKpiId] = useState(null)
  const [kpiList, setKpiList] = useState([])
  const [subKpiList, setSubKpiList] = useState([])
  const [aspects, setAspects] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingSubKpi, setLoadingSubKpi] = useState(false)
  const [filterDates, setFilterDates] = useState([null, null])
  const [showCreate, setShowCreate] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [editData, setEditData] = useState(null)
  const [showAddWeight, setShowAddWeight] = useState(false)
  const [editAspectData, setEditAspectData] = useState(null)
  const [isCreatingRootAspect, setIsCreatingRootAspect] = useState(false)
  const [showCreateKpi, setShowCreateKpi] = useState(false)
  const [createKpiAspectId, setCreateKpiAspectId] = useState(null)
  const [showCreatePersonalKpi, setShowCreatePersonalKpi] = useState(false)
  const [createPersonalKpiAspectId, setCreatePersonalKpiAspectId] = useState(
    null
  )
  const [createChildAspectParentId, setCreateChildAspectParentId] = useState(
    null
  )
  const [expandedNodes, setExpandedNodes] = useState({})

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const fetchAspects = useCallback(async () => {
    try {
      const res = await KpiV2Request.getAllAspects()
      setAspects(res.data?.content ?? res.data ?? [])
    } catch {
      /* silent */
    }
  }, [])

  const fetchKpiList = useCallback(async () => {
    setLoading(true)
    try {
      const params = { size: 200 }
      if (filterDates[0])
        params.start_date = filterDates[0].format('YYYY-MM-DD')
      if (filterDates[1]) params.end_date = filterDates[1].format('YYYY-MM-DD')
      const res = await KpiV2Request.getAll(params)
      setKpiList(res.data?.content ?? res.data ?? [])
    } catch {
      message.error('Không thể tải danh sách KPI.')
    } finally {
      setLoading(false)
    }
  }, [filterDates])

  const fetchSubKpiList = useCallback(async () => {
    setLoadingSubKpi(true)
    try {
      const params = { size: 200 }
      if (filterDates[0])
        params.report_date_from = filterDates[0].format('YYYY-MM-DD')
      if (filterDates[1])
        params.report_date_to = filterDates[1].format('YYYY-MM-DD')
      if (effectiveTab === 'PERSONAL') {
        params.permission = ASSIGNEE_TYPE.HANDLER
      }
      const res = await KpiV2Request.getAllSubKpi(params)
      setSubKpiList(res.data?.content ?? res.data ?? [])
    } catch {
      message.error('Không thể tải danh sách KPI phụ.')
    } finally {
      setLoadingSubKpi(false)
    }
  }, [filterDates, effectiveTab])

  const handleDeleteAspect = async id => {
    try {
      await KpiV2Request.deleteAspect(id)
      message.success('Xóa khía cạnh thành công!')
      fetchAspects()
    } catch (error) {
      message.error(error?.vi || 'Xóa khía cạnh thất bại.')
    }
  }

  useEffect(() => {
    fetchAspects()
  }, [fetchAspects])
  useEffect(() => {
    fetchKpiList()
    fetchSubKpiList()
  }, [fetchKpiList, fetchSubKpiList])

  // ── Auto expand ──────────────────────────────────────────────────────────────
  const isPersonalTab = effectiveTab === 'PERSONAL'
  const isCompanyTab = effectiveTab === 'COMPANY'

  useEffect(() => {
    setExpandedNodes(prev => {
      const next = { ...prev }

      if (isPersonalTab) {
        aspects.forEach(a => {
          next[`asp_${a.id}`] = true
        })
        kpiList.forEach(k => {
          next[`kpi_${k.id}`] = true
        })
        return next
      }

      if (!isCompanyTab) {
        aspects.forEach(a => {
          next[`asp_${a.id}`] = true
        })
        return next
      }

      return next
    })
  }, [effectiveTab, aspects, kpiList])

  const toggleExpandNode = key => {
    setExpandedNodes(prev => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  // ── Tree computation ──────────────────────────────────────────────────────────
  const aspectTree = useMemo(() => {
    const map = {}
    aspects.forEach(asp => {
      map[asp.id] = {
        ...asp,
        type: 'aspect',
        children: [],
        kpis: [],
      }
    })
    kpiList.forEach(kpi => {
      if (kpi.aspect_id && map[kpi.aspect_id]) {
        map[kpi.aspect_id].kpis.push({
          ...kpi,
          type: 'kpi',
          children: [],
        })
      }
    })
    subKpiList.forEach(sub => {
      if (sub.kpi_id) {
        let found = false
        for (const aspId in map) {
          const kpiNode = map[aspId].kpis.find(c => c.id === sub.kpi_id)
          if (kpiNode) {
            kpiNode.children.push({
              ...sub,
              type: 'subKpi',
            })
            found = true
            break
          }
        }
        if (!found && sub.aspect_id && map[sub.aspect_id]) {
          map[sub.aspect_id].kpis.push({
            ...sub,
            type: 'subKpi',
            children: [],
          })
        }
      } else if (sub.aspect_id && map[sub.aspect_id]) {
        map[sub.aspect_id].kpis.push({
          ...sub,
          type: 'subKpi',
          children: [],
        })
      }
    })
    const roots = []
    aspects.forEach(asp => {
      const parentId = asp?.parent_id || asp?.parentId || null
      if (parentId && map[parentId]) map[parentId].children.push(map[asp.id])
      else roots.push(map[asp.id])
    })
    return roots
  }, [aspects, kpiList, subKpiList])

  const activeLoading = loading || loadingSubKpi

  const renderAspectMoreMenu = (aspect, level) => (
    <Menu
      onClick={({ key, domEvent }) => {
        domEvent.stopPropagation()
        if (key === 'add-lv2') {
          setEditAspectData(null)
          setCreateChildAspectParentId(aspect.id)
          setIsCreatingRootAspect(false)
          setShowAddWeight(true)
        } else if (key === 'add-kpi') {
          setCreateKpiAspectId(aspect.id)
          setShowCreateKpi(true)
        } else if (key === 'edit') {
          setEditAspectData(aspect)
          setIsCreatingRootAspect(false)
          setCreateChildAspectParentId(null)
          setShowAddWeight(true)
        } else if (key === 'delete') {
          handleDeleteAspect(aspect.id)
        }
      }}>
      {level === 0 && (
        <Menu.Item
          key="add-lv2"
          icon={<PlusOutlined style={{ color: TOKEN.purple }} />}>
          <span style={{ color: TOKEN.purple }}>Thêm khía cạnh LV2</span>
        </Menu.Item>
      )}
      {level === 1 && (
        <Menu.Item
          key="add-kpi"
          icon={<PlusOutlined style={{ color: TOKEN.blue }} />}>
          <span style={{ color: TOKEN.blue }}>Tạo KPI</span>
        </Menu.Item>
      )}
      <Menu.Divider />
      <Menu.Item key="edit" icon={<EditOutlined />}>
        Chỉnh sửa
      </Menu.Item>
      <Menu.Item key="delete" icon={<DeleteOutlined />} danger>
        <span>Xóa khía cạnh</span>
      </Menu.Item>
    </Menu>
  )

  // ── Expanded KPI/SubKPI detail ────────────────────────────────────────────────
  const renderExpandedDetail = record => (
    <ExpandedDetailContainer>
      {record.type === 'subKpi' ? (
        <KpiV2SubDetailExpand
          subKpiId={record.id}
          aspects={aspects}
          onDeleted={() => {
            setExpandedKpiId(null)
            setExpandedSubKpiId(null)
            fetchSubKpiList()
          }}
          onUpdated={fetchSubKpiList}
        />
      ) : (
        <KpiV2DetailExpand
          kpiId={record.id}
          aspects={aspects}
          onDeleted={() => {
            setExpandedKpiId(null)
            setExpandedSubKpiId(null)
            fetchKpiList()
            fetchSubKpiList()
          }}
          onUpdated={() => {
            fetchKpiList()
            fetchSubKpiList()
          }}
        />
      )}
    </ExpandedDetailContainer>
  )

  // ── KPI row ───────────────────────────────────────────────────────────────────
  const renderKpiNode = (node, level) => {
    const isSubKpi = node.type === 'subKpi'
    const nodeId = isSubKpi ? `sub_${node.id}` : `kpi_${node.id}`
    const isExpanded = !!expandedNodes[nodeId]
    const hasChildren = node.children && node.children.length > 0
    const isActiveDetail = isSubKpi
      ? expandedSubKpiId === node.id
      : expandedKpiId === node.id

    const handleRowClick = () => {
      if (isSubKpi) {
        setExpandedSubKpiId(prev => (prev === node.id ? null : node.id))
        setExpandedKpiId(null)
      } else {
        setExpandedKpiId(prev => (prev === node.id ? null : node.id))
        setExpandedSubKpiId(null)
      }
    }

    const handler = (node.assignees || []).find(
      a => a.permission === ASSIGNEE_TYPE.HANDLER
    )

    return (
      <KpiNodeContainer key={nodeId} level={level}>
        <KpiRowCard active={isActiveDetail} onClick={handleRowClick}>
          <KpiRowContent>
            <KpiRowTop>
              <ExpandIconWrapper
                expanded={isExpanded}
                $hidden={!hasChildren}
                onClick={e => {
                  e.stopPropagation()
                  if (hasChildren) toggleExpandNode(nodeId)
                }}>
                <RightOutlined />
              </ExpandIconWrapper>

              <NodeTypeIcon $color={isSubKpi ? TOKEN.teal : TOKEN.purple}>
                {isSubKpi ? <UserOutlined /> : <ApartmentOutlined />}
              </NodeTypeIcon>

              <NodeTitle>
                <NodeTitleText ellipsis={{ tooltip: node.purpose || '(Chưa có tên)' }}>
                  {node.purpose || '(Chưa có tên)'}
                </NodeTitleText>
                {node.kgi ? (
                  <Tooltip title={node.kgi} placement="topLeft" mouseEnterDelay={0.5}>
                    <KgiBadge>{node.kgi}</KgiBadge>
                  </Tooltip>
                ) : null}
              </NodeTitle>
            </KpiRowTop>

            <NodeMeta>
              {node.indicator_weight != null && (
                <MetaItem>
                  <WeightBar
                    name="Trọng số chỉ tiêu"
                    value={node.indicator_weight}
                    color={isSubKpi ? TOKEN.teal : TOKEN.blue}
                  />
                </MetaItem>
              )}
              {node.overall_weight != null && (
                <MetaItem>
                  <WeightBar
                    name="Trọng số chung"
                    value={node.overall_weight}
                    color={TOKEN.amber}
                  />
                </MetaItem>
              )}
              {/* {node.measurement && (
                <MetaItem width="80px">
                  <Tooltip title={`Đơn vị tính: ${node.measurement}`}>
                    <Tag
                      style={{
                        margin: 0,
                        borderRadius: 6,
                        color: TOKEN.sub,
                        borderColor: TOKEN.border,
                        cursor: 'help',
                      }}>
                      {node.measurement}
                    </Tag>
                  </Tooltip>
                </MetaItem>
              )} */}
              {/* {node.score != null && (
                <MetaItem width="60px">
                  <Tooltip title={`Điểm số: ${node.score}`}>
                    <Tag
                      style={{
                        margin: 0,
                        borderRadius: 6,
                        background: `${SCORE_COLOR(node.score)}18`,
                        color: SCORE_COLOR(node.score),
                        border: `1px solid ${SCORE_COLOR(node.score)}40`,
                        cursor: 'help',
                      }}>
                      {node.score}
                    </Tag>
                  </Tooltip>
                </MetaItem>
              )} */}
              <MetaItem>
                {handler ? (
                  <Tooltip
                    title={`Người xử lý: ${handler.name_uppercase}`}
                    mouseEnterDelay={0.5}>
                    <HandlerWrap>
                      <AvatarHasName
                        imgId={handler.image}
                        name={handler.name_uppercase}
                        size={28}
                      />
                    </HandlerWrap>
                  </Tooltip>
                ) : (
                  <span style={{ color: TOKEN.muted }}>—</span>
                )}
              </MetaItem>
            </NodeMeta>
          </KpiRowContent>
        </KpiRowCard>

        {isActiveDetail && renderExpandedDetail(node)}

        {isExpanded && hasChildren && (
          <KpiChildrenList>
            {node.children.map(child => renderKpiNode(child, level + 1))}
          </KpiChildrenList>
        )}
      </KpiNodeContainer>
    )
  }

  // ── Aspect section ────────────────────────────────────────────────────────────
  const renderAspectSection = (aspect, level = 0) => {
    const aspKey = `asp_${aspect.id}`
    const isExpanded = !!expandedNodes[aspKey]
    const childrenAspects = aspect.children || []
    const kpis = aspect.kpis || []

    let nodesToRender = kpis
    if (isPersonalTab) {
      nodesToRender = []
      kpis.forEach(item => {
        if (item.type === 'subKpi') nodesToRender.push(item)
        else if (item.type === 'kpi' && item.children)
          nodesToRender.push(...item.children)
      })
    } else if (isCompanyTab) {
      nodesToRender = []
    }

    const hasContent = childrenAspects.length > 0 || nodesToRender.length > 0

    // Level-aware accent color
    const levelColor = level === 0 ? TOKEN.blue : TOKEN.purple

    return (
      <AspectCard key={aspect.id} $level={level}>
        <AspectHeaderRow $level={level} onClick={() => toggleExpandNode(aspKey)}>
          <AspectHeaderLeft>
            <ExpandIconWrapper expanded={isExpanded} $hidden={!hasContent}>
              <RightOutlined />
            </ExpandIconWrapper>
            <NodeTypeIcon $color={levelColor}>
              <FolderOpenOutlined />
            </NodeTypeIcon>
            <AspectTitle
              $level={level}
              ellipsis={{ tooltip: aspect.context }}>
              {aspect.context}
            </AspectTitle>
          </AspectHeaderLeft>

          {(aspect.weight != null || !isPersonalTab) && (
            <AspectHeaderRight onClick={e => e.stopPropagation()}>
              {aspect.weight != null && (
                <AspectWeightTag $color={levelColor}>
                  {aspect.weight}%
                </AspectWeightTag>
              )}

              {!isPersonalTab && (
                <AccessControlAction
                  aclActionType={ACL_ACTION_TYPE.create__ASPECT_KPI}>
                  <Dropdown
                    overlay={renderAspectMoreMenu(aspect, level)}
                    trigger={['click']}
                    placement="bottomRight">
                    <MoreMenuTrigger onClick={e => e.stopPropagation()}>
                      <MoreOutlined />
                    </MoreMenuTrigger>
                  </Dropdown>
                </AccessControlAction>
              )}
            </AspectHeaderRight>
          )}
        </AspectHeaderRow>

        {isExpanded && (
          <AspectExpandedContent $hasContent={hasContent}>
            {nodesToRender.length > 0 && (
              <TreeNodeList>
                {nodesToRender.map(node => renderKpiNode(node, level + 1))}
              </TreeNodeList>
            )}
            {childrenAspects.length > 0 && (
              <AspectChildrenList $hasNodes={nodesToRender.length > 0}>
                {childrenAspects.map(child =>
                  renderAspectSection(child, level + 1)
                )}
              </AspectChildrenList>
            )}
          </AspectExpandedContent>
        )}
      </AspectCard>
    )
  }

  // ── Tab bar ────────────────────────────────────────────────────────────────────
  const renderTabBar = () => (
    <TabBar>
      {visibleTabs.map(tab => {
        const isActive = effectiveTab === tab.value
        return (
          <TabItem
            key={tab.value}
            $active={isActive}
            $color={tab.color}
            onClick={() => handleTabChange(tab.value)}>
            <TabLabel $active={isActive} $color={tab.color}>
              {tab.label}
            </TabLabel>
          </TabItem>
        )
      })}
    </TabBar>
  )

  // ── Render ─────────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <Helmet>
        <title>KPI | {systemName}</title>
        <style>{`
          .kpi-table-row:hover td { background: ${TOKEN.hover} !important; }
          .kpi-table-row td { transition: background 0.15s; }
          .ant-table-tbody > tr.ant-table-expanded-row > td {
            background: ${TOKEN.bg}; padding: 0 !important;
          }
        `}</style>
      </Helmet>

      <PageTitle location={props.location}>
        <PageActions>
          {isCompanyTab && (
            <AccessControlAction
              aclActionType={ACL_ACTION_TYPE.create__ASPECT_KPI}>
              <Button
                icon={<PlusOutlined />}
                style={{
                  borderRadius: 8,
                  borderColor: TOKEN.purple,
                  color: TOKEN.purple,
                }}
                onClick={() => {
                  setEditAspectData(null)
                  setCreateChildAspectParentId(null)
                  setIsCreatingRootAspect(true)
                  setShowAddWeight(true)
                }}>
                Thêm trọng số
              </Button>
            </AccessControlAction>
          )}
          {isCompanyTab && (
            <AccessControlAction aclActionType={ACL_ACTION_TYPE.create__KPI}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{
                  borderRadius: 8,
                  background: TOKEN.blue,
                  borderColor: TOKEN.blue,
                }}
                onClick={() => setShowCreate(true)}>
                Tạo KPI mới
              </Button>
            </AccessControlAction>
          )}
        </PageActions>
      </PageTitle>

      <ContentBlockWrapper>
        <KpiContentBlock>
          <Spin spinning={activeLoading}>
            <PageInner>
              {renderTabBar()}

              {effectiveTab === 'PERSONAL' && (
                <FilterCard>
                  <FilterRow>
                    <FilterOutlined style={{ color: TOKEN.teal }} />
                    <FilterLabel>Thời gian báo cáo:</FilterLabel>
                    <FilterDatePickerWrap>
                      <DatePicker.RangePicker
                        value={filterDates}
                        onChange={dates => setFilterDates(dates || [null, null])}
                        format="DD/MM/YYYY"
                        placeholder={['Từ ngày', 'Đến ngày']}
                        allowClear
                      />
                    </FilterDatePickerWrap>
                  </FilterRow>
                </FilterCard>
              )}

              <TreeContainer>
                {aspectTree.map(aspect => renderAspectSection(aspect, 0))}
              </TreeContainer>
            </PageInner>
          </Spin>
        </KpiContentBlock>
      </ContentBlockWrapper>

      {/* Modals */}
      <KpiV2UpsertModal
        visible={showCreate}
        onClose={() => setShowCreate(false)}
        onSuccess={fetchKpiList}
        aspects={aspects}
      />
      <KpiV2UpsertModal
        visible={showEdit}
        onClose={() => {
          setShowEdit(false)
          setEditData(null)
        }}
        onSuccess={fetchKpiList}
        aspects={aspects}
        initialData={editData}
      />
      <KpiV2UpsertModal
        visible={showCreateKpi}
        onClose={() => {
          setShowCreateKpi(false)
          setCreateKpiAspectId(null)
        }}
        onSuccess={fetchKpiList}
        aspects={aspects}
        defaultAspectId={createKpiAspectId}
      />
      <KpiV2SubUpsertModal
        visible={showCreatePersonalKpi}
        onClose={() => {
          setShowCreatePersonalKpi(false)
          setCreatePersonalKpiAspectId(null)
        }}
        onSuccess={fetchSubKpiList}
        aspects={aspects}
        defaultAspectId={createPersonalKpiAspectId}
      />
      <KpiV2AddWeightModal
        visible={showAddWeight}
        onClose={() => {
          setShowAddWeight(false)
          setEditAspectData(null)
          setCreateChildAspectParentId(null)
          setIsCreatingRootAspect(false)
        }}
        onSuccess={fetchAspects}
        editData={editAspectData}
        defaultParentId={createChildAspectParentId}
        forceRoot={isCreatingRootAspect}
      />
    </DashboardLayout>
  )
}

export default inject('aclStore', 'authenticationStore')(observer(KpiV2Page))
